import { Box, Flex, Text, Circle, Separator } from '@chakra-ui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import * as React from 'react';

export interface StepProps {
  title: string;
  description?: string;
  status: 'pending' | 'current' | 'completed';
  onClick?: () => void;
}

export interface StepsProps {
  steps: StepProps[];
  orientation?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
}

const StepIcon = ({ status, size = 'md' }: { status: StepProps['status']; size?: StepsProps['size'] }) => {
  const sizeMap = {
    sm: { circle: '24px', icon: '12px' },
    md: { circle: '32px', icon: '16px' },
    lg: { circle: '40px', icon: '20px' },
  };

  const { circle, icon } = sizeMap[size];

  if (status === 'completed') {
    return (
      <Circle size={circle} bg="green.500" color="white">
        <CheckIcon width={icon} height={icon} />
      </Circle>
    );
  }

  if (status === 'current') {
    return (
      <Circle size={circle} bg="brand.500" color="white" fontWeight="bold">
        <Text fontSize="sm">{/* Step number will be added by parent */}</Text>
      </Circle>
    );
  }

  return (
    <Circle size={circle} bg="gray.200" color="gray.500">
      <Text fontSize="sm">{/* Step number will be added by parent */}</Text>
    </Circle>
  );
};

export const Step = ({ title, description, status, onClick }: StepProps) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      _hover={onClick ? { opacity: 0.8 } : {}}
    >
      <StepIcon status={status} />
      <Box ml={3}>
        <Text
          fontSize="sm"
          fontWeight={status === 'current' ? 'semibold' : 'normal'}
          color={status === 'completed' ? 'green.600' : status === 'current' ? 'brand.600' : 'gray.500'}
        >
          {title}
        </Text>
        {description && (
          <Text fontSize="xs" color="gray.500">
            {description}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export const Steps = ({ steps, orientation = 'horizontal' }: StepsProps) => {
  return (
    <Box>
      {orientation === 'horizontal' ? (
        <Flex alignItems="center" gap={4}>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <Step {...step} />
              {index < steps.length - 1 && (
                <Separator orientation="horizontal" flex="1" minW="20px" />
              )}
            </React.Fragment>
          ))}
        </Flex>
      ) : (
        <Flex direction="column" gap={4}>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <Step {...step} />
              {index < steps.length - 1 && (
                <Separator orientation="vertical" height="20px" ml={4} />
              )}
            </React.Fragment>
          ))}
        </Flex>
      )}
    </Box>
  );
};
